import { Column } from '../columns/createColumn';
import { CalculationParams } from '../features/settings/reducer';
import { DriverFuelType } from '../pages/driverAnalysis/driverAnalysisReducer';

type RatingMapping = {
    fuel?: keyof CalculationParams;
    electric?: keyof CalculationParams;
};

const SHARED_PARAM_RATINGS: Record<string, RatingMapping> = {
    coastingRating: { fuel: 'coastingRating', electric: 'coastingRating' },
    brakingRating: { fuel: 'brakingRating', electric: 'brakingRating' },
};

const DUAL_ENGINE_RATINGS: Record<string, RatingMapping> = {
    cruiseControlRating: { fuel: 'cruiseControlRating', electric: 'electricCruiseControlRating' },
    cruiseControlRatingV2: { fuel: 'cruiseControlRating', electric: 'electricCruiseControlRating' },
    cruiseControlRatingV3: { fuel: 'cruiseControlRating', electric: 'electricCruiseControlRating' },
    electricCruiseControlRating: {
        fuel: 'cruiseControlRating',
        electric: 'electricCruiseControlRating',
    },
    accelerationPedalRating: {
        fuel: 'accelerationPedalRating',
        electric: 'electricAccelerationPedalRating',
    },
    accelerationPedalRatingV2: {
        fuel: 'accelerationPedalRating',
        electric: 'electricAccelerationPedalRating',
    },
    electricAccelerationPedalRating: {
        fuel: 'accelerationPedalRating',
        electric: 'electricAccelerationPedalRating',
    },
    overspeedRating: { fuel: 'overSpeedRating', electric: 'electricOverSpeedRating' },
    electricOverspeedRating: { fuel: 'overSpeedRating', electric: 'electricOverSpeedRating' },
};

const ELECTRIC_ONLY_RATINGS: Record<string, RatingMapping> = {
    electricRecuperationRating: { electric: 'recuperationRating' },
    recuperationRating: { electric: 'recuperationRating' },
};

const HARSH_KPI_RATINGS: Record<string, RatingMapping> = {
    harshAccelerationRating: {
        fuel: 'harshAccelerationRating',
        electric: 'harshAccelerationRating',
    },
    harshBrakingRating: { fuel: 'harshBrakingRating', electric: 'harshBrakingRating' },
};

const EXCESSIVE_KPI_RATINGS: Record<string, RatingMapping> = {
    excessiveIdlingRating: { fuel: 'excessiveIdlingRating', electric: 'excessiveIdlingRating' },
};

const CONDITIONAL_RATINGS: Record<string, RatingMapping> = {
    ...HARSH_KPI_RATINGS,
    ...EXCESSIVE_KPI_RATINGS,
};

const RATING_MAPPINGS: Record<string, RatingMapping> = {
    ...SHARED_PARAM_RATINGS,
    ...DUAL_ENGINE_RATINGS,
    ...ELECTRIC_ONLY_RATINGS,
    ...CONDITIONAL_RATINGS,
};

// TODO: Remove shouldConsiderElectricParams parameter when ft_treeFilter is permanently enabled
export function shouldShowColumn(
    column: Column,
    calculationParams: CalculationParams,
    selectedFuelType: DriverFuelType,
    shouldShowHarshKPIs: boolean,
    shouldShowExcessiveKPIs: boolean,
    shouldConsiderElectricParams: boolean = true,
): boolean {
    const columnKey = column.key;

    const mapping = RATING_MAPPINGS[columnKey];
    if (!mapping) {
        return true;
    }

    if (!shouldShowHarshKPIs && columnKey in HARSH_KPI_RATINGS) {
        return false;
    }

    if (!shouldShowExcessiveKPIs && columnKey in EXCESSIVE_KPI_RATINGS) {
        return false;
    }

    if (
        !shouldConsiderElectricParams &&
        mapping.electric &&
        mapping.electric in ELECTRIC_ONLY_RATINGS
    ) {
        return (
            selectedFuelType === DriverFuelType.ALL || selectedFuelType === DriverFuelType.ELECTRIC
        );
    }

    switch (selectedFuelType) {
        case DriverFuelType.ALL:
            return isEnabledForAny(mapping, calculationParams, shouldConsiderElectricParams);

        case DriverFuelType.FUEL:
            return isEnabledForFuel(mapping, calculationParams);

        case DriverFuelType.ELECTRIC:
            return isEnabledForElectric(mapping, calculationParams, shouldConsiderElectricParams);

        default:
            return false;
    }
}

// TODO: Remove shouldConsiderElectricParams parameter when ft_treeFilter is permanently enabled
function isEnabledForAny(
    mapping: RatingMapping,
    calculationParams: CalculationParams,
    shouldConsiderElectricParams: boolean = true,
): boolean {
    const fuelEnabled = mapping.fuel ? (calculationParams[mapping.fuel]?.enabled ?? false) : false;
    if (
        !shouldConsiderElectricParams &&
        mapping.electric &&
        mapping.electric in ELECTRIC_ONLY_RATINGS
    ) {
        return true;
    }

    const electricEnabled =
        shouldConsiderElectricParams && mapping.electric
            ? (calculationParams[mapping.electric]?.enabled ?? false)
            : false;

    return fuelEnabled || electricEnabled;
}

function isEnabledForFuel(mapping: RatingMapping, calculationParams: CalculationParams): boolean {
    if (!mapping.fuel) {
        return false;
    }

    return calculationParams[mapping.fuel]?.enabled ?? false;
}

function isEnabledForElectric(
    mapping: RatingMapping,
    calculationParams: CalculationParams,
    shouldConsiderElectricParams: boolean = true,
): boolean {
    if (!mapping.electric) {
        return false;
    }

    if (!shouldConsiderElectricParams && mapping.electric in ELECTRIC_ONLY_RATINGS) {
        return true;
    }

    return calculationParams[mapping.electric]?.enabled ?? false;
}

export function shouldShowDieselValue(
    columnKey: string,
    calculationParams: CalculationParams,
): boolean {
    const mapping = RATING_MAPPINGS[columnKey];

    if (!mapping || !mapping.fuel) {
        return true;
    }

    return calculationParams[mapping.fuel]?.enabled ?? false;
}

export function shouldShowElectricValue(
    columnKey: string,
    calculationParams: CalculationParams,
    shouldConsiderElectricParams: boolean = true,
): boolean {
    const mapping = RATING_MAPPINGS[columnKey];

    if (!mapping || !mapping.electric) {
        return true;
    }

    if (!shouldConsiderElectricParams && mapping.electric in ELECTRIC_ONLY_RATINGS) {
        return true;
    }

    return calculationParams[mapping.electric]?.enabled ?? false;
}
